import { Image } from '@/components/shared/Image';
import { useAuthentication } from '@/hooks/useAuthentication';
import { api } from '@/utils/api';
import { Box, Container, Flex, Heading, Select, Text, Tooltip } from '@chakra-ui/react';
import { compareAddresses, formatAddress } from '@onbeam/utils';
import { UserRole } from '@prisma/client';
import { CopyButton, VerifiedBadge } from '@sphere/ui';
import Case from 'case';
import useTranslation from 'next-translate/useTranslation';
import { rem } from 'polished';
import { ChangeEvent, type FC } from 'react';
import { toast } from 'sonner';
import { Address, checksumAddress } from 'viem';

type Props = {
  address: string;
  userId?: string;
  role?: UserRole;
  displayName?: string;
  avatar?: string;
};

export const AccountShell: FC<Props> = ({ address, userId, role, displayName, avatar }) => {
  const { t } = useTranslation('account');
  const { userAddress, user } = useAuthentication();
  const isOwner = compareAddresses(userAddress, address);
  const setUserRole = api.admin.setUserRole.useMutation();
  const formattedAddress = formatAddress(address);

  const handleChangeRole = async (e: ChangeEvent<HTMLSelectElement>) => {
    if (!userId) return;

    try {
      await setUserRole.mutateAsync({ id: userId, role: e.target.value as UserRole });
      toast.success(t('profile.set-role.success'));
    } catch (error) {
      toast.error(t('profile.set-role.failed'));
      if (role) {
        e.target.value = role;
      }
    }
  };

  return (
    <Container
      display={{ md: 'flex' }}
      flexDirection={{ md: 'row' }}
      variant="breakout"
      gap="space.20"
    >
      <Box
        display="flex"
        alignItems={{ base: 'center', md: 'flex-start' }}
        mb={{ base: 'space.16', md: 0 }}
      >
        <Image
          src={avatar}
          fallback="/images/default-profile-image.png"
          alt={t('profile.avatar', { user: displayName || formattedAddress })}
          objectFit="cover"
          width={100}
          height={100}
          boxSize={{ base: rem(72), md: rem(96) }}
          rounded="full"
        />
        <CopyButton
          ml="space.12"
          mr="space.8"
          display={{ base: 'flex', md: 'none' }}
          content={checksumAddress(address as Address)}
          tooltipContent={{
            success: t('profile.copy-address.success'),
            failed: t('profile.copy-address.failed'),
          }}
          alignItems="flex-end"
          iconProps={{ boxSize: rem(12), mb: 'space.8' }}
        >
          <Heading as="h1" noOfLines={1}>
            {displayName || formattedAddress}
          </Heading>
        </CopyButton>
        {role === UserRole.CREATOR && (
          <VerifiedBadge mt="space.6" display={{ md: 'none' }} boxSize={rem(18)} />
        )}
      </Box>

      <Flex
        flexDir="column"
        justify="center"
        mt={{ base: 'space.8', md: 0 }}
        maxW={{ md: '60%', lg: '70%' }}
        flexGrow="1"
      >
        <Flex display={{ base: 'none', md: 'flex' }} align="center" gap="space.12" mb="space.8">
          <Tooltip
            label={t('profile.copy-address.tooltip')}
            placement="top"
            hasArrow
            arrowSize={8}
            openDelay={300}
          >
            <Heading as="h1" fontSize="3xl" display={{ base: 'none', md: 'block' }} maxW="full">
              <CopyButton
                content={address}
                tooltipContent={{
                  success: t('profile.copy-address.success'),
                  failed: t('profile.copy-address.failed'),
                }}
                fontWeight="unset"
                alignItems="flex-end"
                iconProps={{ boxSize: rem(14), mb: 'space.12' }}
              >
                <Text noOfLines={1}>{displayName || formattedAddress}</Text>
              </CopyButton>
            </Heading>
          </Tooltip>
          {role === UserRole.CREATOR && <VerifiedBadge mt="space.4" boxSize={rem(20)} />}
        </Flex>
        {user?.role === UserRole.ADMIN && !isOwner && !!userId && (
          <>
            <Text fontSize="xs" mb="space.2">
              {t('profile.set-role.label')}
            </Text>
            <Select
              w={rem(82)}
              size="xs"
              rounded="md"
              defaultValue={role}
              onChange={handleChangeRole}
            >
              {Object.values(UserRole).map(role => (
                <option key={role} value={role}>
                  {Case.pascal(role)}
                </option>
              ))}
            </Select>
          </>
        )}
      </Flex>
    </Container>
  );
};
